import type { FileRecord } from "@cohesive/api-client";

/**
 * @deprecated Remove once historical `type: "artifact"` entities have been
 * rewritten to `base: "file_record"` in Loro canvas state and
 * `flow_messages.result.entities[]`.
 *
 * Synthesizes a `FileRecord`-shaped object from a legacy artifact entity's
 * `data` payload so we can render every artifact-ish entity through the
 * shared `FileRecordCard`. Pure — no hooks, no fetches; just data reshape.
 *
 * Why this works: the artifact-to-file-record migration script reuses the
 * artifact UUID as the file_record id, so any historical reference to
 * `entity.data.id` resolves to a real file_record on the backend. The
 * synthesized record carries enough fields for the card preview; click
 * handlers should route through `useOpenFileRecord(data.id)` which fetches
 * the real, fully-hydrated file_record on demand.
 */
export function legacyArtifactEntityDataToFileRecord(
  data: Record<string, unknown>,
): FileRecord {
  const now = new Date().toISOString();

  const id = typeof data.id === "string" ? data.id : "legacy-artifact";

  const rawTitle = typeof data.title === "string" ? data.title.trim() : "";
  const title = rawTitle || "Untitled";

  const formatting =
    typeof data.formatting === "string" ? data.formatting : "markdown";
  const isHtml = formatting === "html";
  const ext = isHtml ? ".html" : ".md";
  const contentType = isHtml ? "text/html" : "text/markdown";

  const content = typeof data.content === "string" ? data.content : "";
  const size = content.length;

  const status: FileRecord["status"] =
    data.status === "deleted" ? "deleted" : "processed";

  const publicUrl =
    typeof data.public_url === "string" && data.public_url
      ? data.public_url
      : undefined;

  const path = `${title}${ext}`;

  return {
    id,
    organization_id: "",
    provider: "s3",
    provider_id: "",
    status,
    // Most artifacts originate in flow sessions; canvas-scoped artifacts will
    // be reclassified by the real file_record once it loads via the click
    // handler. The class is mostly cosmetic for the inline card preview.
    class: "flow-session",
    path,
    content_type: contentType,
    content_hash: "",
    size,
    ...(publicUrl ? { url: publicUrl, published: true } : {}),
    created_at: typeof data.created_at === "string" ? data.created_at : now,
    updated_at: typeof data.updated_at === "string" ? data.updated_at : now,
  };
}
